import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Icon, Button } from "semantic-ui-react";
import _ from "lodash";
import cx from "classnames";
import { LuRefreshCcw } from "react-icons/lu";
import { fetchGeneratedReport } from "actions/generateReport";
import InfiniteScroll from "components/ui/InfiniteScroll";
import Truncate from "react-truncate";
import { Image } from 'react-bootstrap';
import styles from "./assets.transaction.module.scss";
import { fetchAllAssets, invalideReTriggerAsset, triggerReprocessingForAsset } from "actions/assetManagement";
import { toast } from "react-toastify";

import ConfirmModal from "components/ui/ConfirmModal";
import SaveChanges from "components/ui/Buttons/SaveButton";

import GoogleDriveIcon from "images/datasources/gdrive.png"
import DropBoxIcon from "images/datasources/dropbox.png"
import BoxIcon from "images/datasources/box.png"
import SharePointIcon from "images/datasources/sharepoint.png"
import OneDriveIcon from "images/datasources/onedrive.png"
import RssFeedIcon from "images/datasources/rss.png"
import { formatTimeTaken } from "components/features/Util/Util";

const TransactionRecords = ({ account, filters, setFilters }) => {

  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [disableSave, setDisableSave] = useState(true);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [page, setPage] = useState(0);

  const reprocessingAsset = useSelector((state) => _.get(state, "accountAssetTransactions.reprocessingAsset", {}));
  const assets = useSelector((state) => _.get(state, "accountAssetTransactions.items", []));
  const loading = useSelector((state) => _.get(state, "accountAssetTransactions.loading", false));
  const currentPage = useSelector((state) =>
    _.get(state, "accountAssetTransactions.pagination.currentPage", 0)
  );
  const totalPages = useSelector((state) =>
    _.get(state, "accountAssetTransactions.pagination.totalPages", 1)
  );

  useEffect(() => {
    if (!_.get(reprocessingAsset, "processing", false)) {
      if (_.get(reprocessingAsset, "success", true)) {
        dispatch(invalideReTriggerAsset());
        toast.success("Successfully processed asset");
      } else if (_.get(reprocessingAsset, "error")) {
        dispatch(invalideReTriggerAsset());
        toast.error(reprocessingAsset.error);
      }
      onReset();
    }
  }, [reprocessingAsset]);


  useEffect(() => {
    setPage(0);
  }, [filters]);


  useEffect(() => {
    console.log("asset transaction response:", assets);
  }, [assets]);

  useEffect(() => {
    if (account && account.id) {
      dispatch(
        fetchAllAssets({
          page,
          limit: 20,
          filters: { ...filters, accountId: account?.id },
        })
      );
    }

  }, [account?.id, filters, page]);

  const onSaveChanges = () => {
    if (!selectedAssetId) {
      onReset()
      return;
    };
    dispatch(triggerReprocessingForAsset({ accountId: account.id, assetId: selectedAssetId }));
    setSelectedAssetId(null);
  };

  const onReset = () => {
    setDisableSave(true);
    setOpen(false);
    setSelectedAssetId(null);
  };

  const handleSortChange = (sortKey) => {
    const isSameKey = filters.sort === sortKey;
    const newOrder = isSameKey && filters.order === "ASC" ? "DESC" : "ASC";
    setFilters((prev) => ({
      ...prev,
      sort: sortKey,
      order: newOrder,
    }));
  };

  const triggerReprocessing = (assetId) => {
    setSelectedAssetId(assetId);
    setOpen(true);
  };


  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "#21c339";
      case "PROCESSING":
        return "#FE9D44";
      case "ERROR":
        return "#e51058";
      default:
        return "gray";
    }
  };


  const showIcon = (storageType) => {

    switch (storageType) {
      case "gdrive":
        return GoogleDriveIcon;
      case "dropbox":
        return DropBoxIcon;
      case "onedrive":
        return OneDriveIcon;
      case "box":
        return BoxIcon;
      case "sharepoint":
        return SharePointIcon;
      case "rss":
        return RssFeedIcon;

      default:
    }
    return null;
  }

  const renderTableBody = () => {
    if (loading && page === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan="6" className={cx(styles.cell)}>
            <div className={cx(styles.loader)}>
              <Icon name="sync" loading className={cx(styles.icon)} />
              Loading...
            </div>
          </Table.Cell>
        </Table.Row>
      );
    }

    if (!loading && _.size(assets) === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan="7" className={cx(styles.cell)}>
            <div className={cx(styles.noData)}>No Data</div>
          </Table.Cell>
        </Table.Row>
      );
    }

    return assets.map((item, idx) => (
      <Table.Row key={idx}>
        <Table.Cell className={cx(styles.cellFirst)} onCopy={(e) => {
          e.preventDefault();
          e.clipboardData.setData('text/plain', item.name || 'N/A');
        }}>
          <div className={cx(styles.cellFirstChildFirst)}>
            <Image src={showIcon(item.datasourceType)} className={styles.imageWidth} />
            &nbsp;
            <Truncate title={item.name} width={140} lines={1} className={cx(styles.text)}>{item.name || "N/A"}</Truncate>
          </div>
          <div className={cx(styles.cellFirstChildTwo)}>{item.fileType || "N/A"}</div>
        </Table.Cell>

        <Table.Cell className={cx(styles.text)} onCopy={(e) => {
          e.preventDefault();
          e.clipboardData.setData('text/plain', item.creator || 'N/A');
        }}>
          <Truncate title={item.creator} width={140} lines={2} className={cx(styles.text)}>
            {item.creator || "N/A"}
          </Truncate>
        </Table.Cell>

        <Table.Cell className={cx(styles.cell)}>
          <div className={cx(styles.text)}>
            {item.processingCreatedDate ? new Date(item.processingCreatedDate).toLocaleDateString() : "N/A"}
          </div>
        </Table.Cell>

        <Table.Cell className={cx(styles.cell)}>
          <div className={cx(styles.text)}>
            {item.modifiedDate ? new Date(item.modifiedDate).toLocaleDateString() : "N/A"}
          </div>
        </Table.Cell>

        <Table.Cell className={cx(styles.text)} onCopy={(e) => {
          e.preventDefault();
          e.clipboardData.setData('text/plain', formatTimeTaken(item.timeTaken || 0));
        }}>
          <Truncate width={140} lines={2} className={cx(styles.text)}>
            {formatTimeTaken(item.totalTime) || "N/A"}
          </Truncate>
        </Table.Cell>

        <Table.Cell className={cx(styles.cell)}>
          <section className={cx(styles.statusCell)}>
            <div>{item.transactionStatus || "N/A"}</div>
            <button onClick={() => triggerReprocessing(item.assetId)} className={cx(styles.resetButton)}>
              <LuRefreshCcw />
            </button>
          </section>
        </Table.Cell>
      </Table.Row>
    ));
  };



  return (
    <InfiniteScroll
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      pagination={true}
      setPage={setPage}
    >
      <Table singleLine className={cx(styles.table)}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={4} className={cx(styles.header)}>NAME</Table.HeaderCell>
            <Table.HeaderCell width={4} className={cx(styles.header)}>CREATED BY</Table.HeaderCell>
            <Table.HeaderCell width={2} className={cx(styles.header)}>
              <div className={cx(styles.sort)}>
                TRANSACTION CREATED
                <span>
                  <Icon
                    name="arrow up"
                    className={cx(
                      styles.icon,
                      filters.sort === "created_date" && filters.order === "ASC" && styles.active
                    )}
                    onClick={() => handleSortChange("created_date")}
                  />
                  <Icon
                    name="arrow down"
                    className={cx(
                      styles.icon,
                      filters.sort === "created_date" && filters.order === "DESC" && styles.active
                    )}
                    onClick={() => handleSortChange("created_date")}
                  />
                </span>
              </div>
            </Table.HeaderCell>
            <Table.HeaderCell width={2} className={cx(styles.header)}>
              <div className={cx(styles.sort)}>
                ASSET MODIFIED
                <span>
                  <Icon
                    name="arrow up"
                    className={cx(
                      styles.icon,
                      filters.sort === "modified_date" && filters.order === "ASC" && styles.active
                    )}
                    onClick={() => handleSortChange("modified_date")}
                  />
                  <Icon
                    name="arrow down"
                    className={cx(
                      styles.icon,
                      filters.sort === "modified_date" && filters.order === "DESC" && styles.active
                    )}
                    onClick={() => handleSortChange("modified_date")}
                  />
                </span>
              </div>
            </Table.HeaderCell>
            <Table.HeaderCell width={4} className={cx(styles.header)}>TIME TAKEN</Table.HeaderCell>
            <Table.HeaderCell width={3} className={cx(styles.header)}>TRANSACTION STATUS</Table.HeaderCell>

          </Table.Row>
        </Table.Header>

        <Table.Body className={cx(styles.body)}>{renderTableBody()}</Table.Body>
        {open && (
          <ConfirmModal
            open={open}
            setOpen={setOpen}
            onSave={onSaveChanges}
            onCancel={onReset}
            content={`Are you sure you want to re-process this asset?`}
            disabled={!selectedAssetId}
          />
        )}
      </Table>
    </InfiniteScroll>

  );
};

export default TransactionRecords;